// Simulation bundle guardrails (docs/design/simulation-upload-design.md §6
// "Guardrails" + §2.2 "Path → object key resolution"). Single source of
// truth for the extension→MIME table, size/count/rate caps, and the path
// validator — shared by `apps/api`'s create (`POST /v1/simulations`),
// finalise, and serve (`GET /v1/sim/:bundleId/:token/*`) routes so the same
// rules are enforced at upload time and at serve time. Framework-agnostic
// (no Hono/Worker imports) so it is also importable from tests and, if ever
// needed, `apps/web`.
//
// Do not hand-edit call sites that duplicate these numbers — retune here so
// the PO can retune every cap without a code hunt (§6 closing note).

/** §6 "Max total bundle size" — 150 MB (largest sample 80.1 MB, ~1.9x
 * headroom). Enforced as a declared-sum check at `POST /v1/simulations` and
 * re-checked against the REAL summed `list()` sizes at finalise. */
export const SIMULATION_BUNDLE_MAX_TOTAL_BYTES = 150 * 1024 * 1024;

/** §6 "Max single file" — 100 MB (largest sample ~43.9 MB). Enforced at both
 * create (declared size) and finalise (real size). */
export const SIMULATION_BUNDLE_MAX_FILE_BYTES = 100 * 1024 * 1024;

/** §6 "Max file count" — 500 (samples are 17). Enforced at create. */
export const SIMULATION_BUNDLE_MAX_FILE_COUNT = 500;

/** §6 / §2.2 "Max path depth / length" — 8 segments, 200 chars total.
 * Enforced at create (manifest paths) AND serve (`rest` after the
 * `bundleId`/token prefix) — see {@link validateSimulationBundlePath}. */
export const SIMULATION_BUNDLE_MAX_PATH_SEGMENTS = 8;
export const SIMULATION_BUNDLE_MAX_PATH_LENGTH = 200;

/** §2.2 per-segment charset: `^[A-Za-z0-9._-]{1,80}$`. */
export const SIMULATION_BUNDLE_PATH_SEGMENT_PATTERN = /^[A-Za-z0-9._-]{1,80}$/;

/** §6 "Upload rate limit" — new bucket: 5 bundle creates per user per 10 min
 * (`SIMULATION_UPLOAD_RATE_LIMIT_*` in `middleware/rateLimit.ts`). */
export const SIMULATION_UPLOAD_RATE_LIMIT_MAX_REQUESTS = 5;
export const SIMULATION_UPLOAD_RATE_LIMIT_WINDOW_SECONDS = 10 * 60;

/** §6 "Concurrent unfinalised bundles" — at most 3 `PENDING` bundles per
 * user; a 4th create 429s until one finalises or ages out. */
export const SIMULATION_BUNDLE_MAX_PENDING_PER_USER = 3;

/** §6 "Re-mint (upload-urls)" — 20/min per user. */
export const SIMULATION_UPLOAD_URLS_RATE_LIMIT_MAX_REQUESTS = 20;
export const SIMULATION_UPLOAD_URLS_RATE_LIMIT_WINDOW_SECONDS = 60;

/** §6 "Serve rate limit" — new bucket: 600 req/min per user
 * (`SIMULATION_ASSET_RATE_LIMIT_*`; must NOT reuse the 60/min signed-url
 * bucket or the 240/min stream bucket). */
export const SIMULATION_ASSET_RATE_LIMIT_MAX_REQUESTS = 600;
export const SIMULATION_ASSET_RATE_LIMIT_WINDOW_SECONDS = 60;

/**
 * §6 "Extension allowlist" — allowlist-only, anything else rejected. Covers
 * every sample type (html, js, css, png, ico, wasm, data, gz) plus plausible
 * future ones (jpg/svg/json/woff etc.). Keys are lower-cased, no leading dot.
 *
 * A trailing `.gz`/`.br` on top of an otherwise-allowed extension is handled
 * separately by {@link stripSimulationBundleCompressionSuffix} — this table
 * intentionally does NOT contain `gz`/`br` as first-class extensions, so
 * `foo.exe.gz` still resolves its INNER extension (`exe`) and is rejected.
 *
 * MIME values double as the §2.3 server-derived Content-Type table (the
 * `.wasm → application/wasm` mapping is mandatory: Unity's
 * `WebAssembly.instantiateStreaming` hard-fails on any other type).
 */
export const SIMULATION_BUNDLE_EXTENSION_MIME_MAP: Readonly<Record<string, string>> = {
  html: 'text/html; charset=utf-8',
  htm: 'text/html; charset=utf-8',
  js: 'text/javascript',
  mjs: 'text/javascript',
  css: 'text/css; charset=utf-8',
  json: 'application/json',
  wasm: 'application/wasm',
  data: 'application/octet-stream',
  unityweb: 'application/octet-stream',
  mem: 'application/octet-stream',
  txt: 'text/plain; charset=utf-8',
  csv: 'text/csv; charset=utf-8',
  xml: 'application/xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  avif: 'image/avif',
  ico: 'image/x-icon',
  bmp: 'image/bmp',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  mp4: 'video/mp4',
  webm: 'video/webm',
};

/** §2.4 "`.gz` handling" — compression suffixes accepted on top of an
 * otherwise-allowed extension (`foo.wasm.gz` OK, `foo.exe.gz` rejected
 * because `exe` alone is not in {@link SIMULATION_BUNDLE_EXTENSION_MIME_MAP}). */
export const SIMULATION_BUNDLE_COMPRESSION_SUFFIXES = ['gz', 'br'] as const;
export type SimulationBundleCompressionSuffix =
  (typeof SIMULATION_BUNDLE_COMPRESSION_SUFFIXES)[number];

function lowerExtensionOf(path: string): string | null {
  const lastDot = path.lastIndexOf('.');
  if (lastDot === -1 || lastDot === path.length - 1) return null;
  return path.slice(lastDot + 1).toLowerCase();
}

/**
 * Splits a trailing `.gz`/`.br` compression suffix off `path`, if present,
 * and resolves the allowlist status/MIME type from the INNER extension
 * (§2.4). Returns `null` compression when there is no recognised
 * compression suffix (the whole path is then checked against the allowlist
 * as-is by the caller).
 */
export function resolveSimulationBundleFileType(path: string): {
  allowed: boolean;
  mimeType: string | null;
  compression: SimulationBundleCompressionSuffix | null;
} {
  const outerExt = lowerExtensionOf(path);
  if (outerExt && (SIMULATION_BUNDLE_COMPRESSION_SUFFIXES as readonly string[]).includes(outerExt)) {
    const inner = path.slice(0, path.length - outerExt.length - 1);
    const innerExt = lowerExtensionOf(inner);
    const mimeType = innerExt ? (SIMULATION_BUNDLE_EXTENSION_MIME_MAP[innerExt] ?? null) : null;
    return {
      allowed: mimeType !== null,
      mimeType,
      compression: outerExt as SimulationBundleCompressionSuffix,
    };
  }

  const mimeType = outerExt ? (SIMULATION_BUNDLE_EXTENSION_MIME_MAP[outerExt] ?? null) : null;
  return { allowed: mimeType !== null, mimeType, compression: null };
}

/**
 * §2.2 "Normalise and validate `rest`" — a pure, reusable validator for a
 * single bundle-relative path, used identically by the create-time manifest
 * check and the serve-time path resolution. Rejects (does not canonicalise)
 * anything unsafe: fail closed, no `path.resolve`-style collapsing, so there
 * is no normalisation-equivalence bug class to reason about.
 *
 * Input is the RAW (not yet `decodeURIComponent`-ed) path segment(s) exactly
 * as received; this function performs the single `decodeURIComponent` pass
 * itself and re-checks for residual `%`/`\`/control chars afterwards.
 *
 * Returns `{ valid: true, normalized }` (forward-slash-joined, no leading
 * slash) on success, or `{ valid: false, reason }` on any rejection — never
 * throws.
 */
export function validateSimulationBundlePath(
  rawPath: string,
): { valid: true; normalized: string } | { valid: false; reason: string } {
  if (rawPath.length === 0) {
    return { valid: false, reason: 'empty path' };
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(rawPath);
  } catch {
    return { valid: false, reason: 'malformed percent-encoding' };
  }

  if (decoded.includes('%')) {
    return { valid: false, reason: 'residual percent-encoding after decode' };
  }
  if (decoded.includes('\\')) {
    return { valid: false, reason: 'backslash not allowed' };
  }
  // eslint-disable-next-line no-control-regex -- deliberately matching NUL/control chars to reject them
  if (/[\x00-\x1f\x7f]/.test(decoded)) {
    return { valid: false, reason: 'control character not allowed' };
  }
  if (decoded.startsWith('/')) {
    return { valid: false, reason: 'absolute path not allowed' };
  }
  if (decoded.length > SIMULATION_BUNDLE_MAX_PATH_LENGTH) {
    return { valid: false, reason: `path exceeds ${SIMULATION_BUNDLE_MAX_PATH_LENGTH} chars` };
  }

  const segments = decoded.split('/');
  if (segments.length > SIMULATION_BUNDLE_MAX_PATH_SEGMENTS) {
    return {
      valid: false,
      reason: `path exceeds ${SIMULATION_BUNDLE_MAX_PATH_SEGMENTS} segments`,
    };
  }

  for (const segment of segments) {
    if (segment.length === 0) {
      return { valid: false, reason: 'empty path segment (double slash)' };
    }
    if (segment === '.' || segment === '..') {
      return { valid: false, reason: '"." / ".." segment not allowed' };
    }
    if (!SIMULATION_BUNDLE_PATH_SEGMENT_PATTERN.test(segment)) {
      return { valid: false, reason: `segment "${segment}" fails allowed charset` };
    }
  }

  return { valid: true, normalized: segments.join('/') };
}
